import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import {
  Card,
  CardBody,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Spinner,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  RadioGroup,
  Radio,
  Tooltip,
  Select,
  SelectItem,
  Textarea,
} from '@nextui-org/react';
import React, { useMemo } from 'react';
import {
  getClaimsByExporterId,
  getClaimById,
  updateClaimStatus,
  updateClaimStatusAndAmount,
  distributeClaimStatus,
  undistributeClaimStatus,
  deleteClaim,
} from 'common/src/api/claims';
import { supabase } from 'common/src/supabase';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import { Icon } from '@iconify/react';
import { formatMoneyUSD } from '../../../utils/functions';
import useFilterStore from '../../../../../common/src/stores/useFiltersStore';
import SeasonFilter from '../../../../../common/src/components/filters/SeasonFilter';
import StatusFilter from '../../../../../common/src/components/filters/StatusFilter';
import InformationTooltip from '../../../../../common/src/components/ui/InformationTooltip';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import { toast } from 'react-toastify';
import Header from '../../../components/layouts/Header';
import {
  Alert,
  EmptyState,
  LoadingState,
  PageLayout,
  useConfirm,
} from '../../../components/ui';

type ManualDistribution = 'byKg' | 'bySales';

type QualitativeField =
  | 'variety'
  | 'caliber'
  | 'color'
  | 'category'
  | 'format'
  | 'label'
  | 'pallet'
  | 'incoterm';

const QUALITATIVE_FIELDS: {
  key: QualitativeField;
  label: string;
}[] = [
  { key: 'variety', label: 'Variedad' },
  { key: 'caliber', label: 'Calibre' },
  { key: 'color', label: 'Color' },
  { key: 'category', label: 'Categoría' },
  { key: 'format', label: 'Formato' },
  { key: 'label', label: 'Etiqueta' },
  { key: 'pallet', label: 'Pallet' },
  { key: 'incoterm', label: 'Incoterm' },
];

type ManualFilters = Record<QualitativeField, string[]>;

interface ManualSettlementDetail {
  id: number;
  settlement_id: number;
  variety?: string | null;
  caliber?: string | null;
  color?: string | null;
  category?: string | null;
  format?: string | null;
  label?: string | null;
  pallet?: string | null;
  incoterm?: string | null;
  kilos?: number | null;
  local_total_price?: number | null;
}

interface ManualSettlement {
  id: number;
  reference_number?: string | null;
  container_number?: string | null;
  receiver?: string | null;
  date_sold?: string | null;
  exchange_rate?: number | null;
  is_blocked?: boolean | null;
  settlement_details?: ManualSettlementDetail[];
}

interface ManualGroup {
  id: string;
  name: string;
  amountUsd: string;
  distribution: ManualDistribution;
  filters: ManualFilters;
}

const emptyManualFilters = (): ManualFilters =>
  QUALITATIVE_FIELDS.reduce((acc, field) => {
    acc[field.key] = [];
    return acc;
  }, {} as ManualFilters);

const roundMoney = (value: number) => Math.round(value * 100) / 100;

interface ClaimDetail {
  id: number;
  claim_id: number;
  settlement_detail_id: number;
  comparable_fob_per_kg: number;
  comparable_kilos: number;
  own_value_per_kg?: number;
  settlements_details?: {
    id?: number;
    variety?: string;
    caliber?: string;
    format?: string;
    color?: string;
    label?: string;
    kilos?: number;
    usd_total_fob?: number;
    settlement_id?: number;
    settlements?: {
      id?: number;
      exchange_rate?: number;
      reference_number?: string;
      ship_trips?: unknown;
      date_sold?: string;
    };
  };
}

interface Claim {
  id: number;
  exporter_id: number;
  receiver: string;
  season: string;
  status: string;
  notes?: string;
  description?: string;
  created_at: string;
  distributed_at?: string | null;
  comparison_type?: string;
  product_definition?: string;
  total_claim_usd?: number;
  total_accepted_usd?: number;
  currency?: string;
  claims_details?: ClaimDetail[];
}

const ClaimsPage = () => {
  const { t } = useTranslation('settlementsComparative');
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const [claims, setClaims] = useState<Claim[]>([]);
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(true);
  const [filterValue, setFilterValue] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [, setLocation] = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDistributeOpen,
    onOpen: onDistributeOpen,
    onClose: onDistributeClose,
  } = useDisclosure();
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [distributeClaim, setDistributeClaim] = useState<Claim | null>(null);
  const [isDistributeLoading, setIsDistributeLoading] = useState<number | null>(
    null
  );
  const [isDistributing, setIsDistributing] = useState(false);
  const [exchangeRateMode, setExchangeRateMode] = useState<
    'fixed' | 'per_settlement'
  >('per_settlement');
  const [fixedExchangeRate, setFixedExchangeRate] = useState<string>('');
  const [acceptedAmount, setAcceptedAmount] = useState<string>('');
  const [isApproving, setIsApproving] = useState(false);
  const [isStatusLoading, setIsStatusLoading] = useState<number | null>(null);
  const [isUndistributeLoading, setIsUndistributeLoading] = useState<
    number | null
  >(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState<number | null>(null);
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [claimToDelete, setClaimToDelete] = useState<Claim | null>(null);
  const {
    isOpen: isManualOpen,
    onOpen: onManualOpen,
    onClose: onManualClose,
  } = useDisclosure();
  const [manualSettlements, setManualSettlements] = useState<
    ManualSettlement[]
  >([]);
  const [selectedManualSettlementIds, setSelectedManualSettlementIds] =
    useState<Set<string>>(new Set());
  const [manualGroups, setManualGroups] = useState<ManualGroup[]>([
    {
      id: crypto.randomUUID(),
      name: 'Grupo 1',
      amountUsd: '',
      distribution: 'bySales',
      filters: emptyManualFilters(),
    },
  ]);
  const [manualDescription, setManualDescription] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [isSavingManual, setIsSavingManual] = useState(false);
  const [manualExchangeRateMode, setManualExchangeRateMode] = useState<
    'per_settlement' | 'fixed'
  >('per_settlement');
  const [manualFixedExchangeRate, setManualFixedExchangeRate] = useState('');
  const [manualSettlementSearch, setManualSettlementSearch] = useState('');
  const [manualReceiverFilter, setManualReceiverFilter] = useState<Set<string>>(
    new Set()
  );

  const fetchClaims = useCallback(async () => {
    if (!userProfile?.exporter_id) return;

    setIsLoading(true);
    try {
      const claimsData = await getClaimsByExporterId(+userProfile.exporter_id);
      const filteredClaims = claimsData.filter((claim) => {
        const seasonMatch = !selectedSeason || claim.season === selectedSeason;
        const statusMatch =
          !selectedStatus ||
          selectedStatus === '' ||
          claim.status === selectedStatus;

        return seasonMatch && statusMatch;
      });

      setClaims(filteredClaims);
    } catch (error) {
      console.error('Error fetching claims:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSeason, selectedStatus, userProfile?.exporter_id]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  useEffect(() => {
    const fetchManualSettlements = async () => {
      if (!userProfile?.exporter_id || !selectedSeason) {
        setManualSettlements([]);
        return;
      }

      const { data, error } = await supabase
        .from('settlements')
        .select(
          `
          id,
          reference_number,
          container_number,
          receiver,
          date_sold,
          exchange_rate,
          is_blocked,
          settlement_details:settlements_details(
            id,
            settlement_id,
            variety,
            caliber,
            color,
            category,
            format,
            label,
            pallet,
            incoterm,
            kilos,
            local_total_price
          )
        `
        )
        .eq('exporter_id', +userProfile.exporter_id)
        .eq('season', selectedSeason)
        .order('date_sold', { ascending: false });

      if (error) {
        console.error(
          'Error fetching settlements for manual injection:',
          error
        );
        toast.error(t('Error al cargar liquidaciones'));
        return;
      }

      setManualSettlements((data || []) as ManualSettlement[]);
    };

    fetchManualSettlements();
  }, [userProfile?.exporter_id, selectedSeason, t]);

  // Filter claims based on search input
  const filteredClaims = claims.filter(
    (claim) =>
      claim.receiver.toLowerCase().includes(filterValue.toLowerCase()) ||
      claim.notes?.toLowerCase().includes(filterValue.toLowerCase()) ||
      claim.id.toString().includes(filterValue)
  );

  const manualReceiverOptions = useMemo(
    () =>
      Array.from(
        new Set(
          manualSettlements
            .map((settlement) => settlement.receiver)
            .filter((receiver): receiver is string => !!receiver)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [manualSettlements]
  );

  const filteredManualSettlements = useMemo(() => {
    const rawSearch = manualSettlementSearch.trim().toLowerCase();
    const searchTokens = rawSearch
      .split(/[\n,;]+/)
      .map((token) => token.trim())
      .filter(Boolean);

    return manualSettlements.filter((settlement) => {
      if (
        manualReceiverFilter.size > 0 &&
        !manualReceiverFilter.has(settlement.receiver || '')
      ) {
        return false;
      }

      if (!rawSearch) return true;

      const haystack = [
        settlement.reference_number,
        settlement.container_number,
        settlement.receiver,
        settlement.date_sold,
        String(settlement.id),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (searchTokens.length > 1) {
        return searchTokens.some((token) => haystack.includes(token));
      }

      return haystack.includes(rawSearch);
    });
  }, [manualReceiverFilter, manualSettlementSearch, manualSettlements]);

  const selectedManualSettlements = useMemo(
    () =>
      manualSettlements.filter((settlement) =>
        selectedManualSettlementIds.has(String(settlement.id))
      ),
    [manualSettlements, selectedManualSettlementIds]
  );

  const selectedManualDetails = useMemo(
    () =>
      selectedManualSettlements.flatMap((settlement) =>
        (settlement.settlement_details || []).map((detail) => ({
          ...detail,
          settlement,
        }))
      ),
    [selectedManualSettlements]
  );

  const manualFieldOptions = useMemo(() => {
    const options = QUALITATIVE_FIELDS.reduce(
      (acc, field) => {
        acc[field.key] = new Set<string>();
        return acc;
      },
      {} as Record<QualitativeField, Set<string>>
    );

    selectedManualDetails.forEach((detail) => {
      QUALITATIVE_FIELDS.forEach((field) => {
        const value = detail[field.key];
        if (value != null && String(value).trim() !== '') {
          options[field.key].add(String(value));
        }
      });
    });

    return QUALITATIVE_FIELDS.reduce(
      (acc, field) => {
        acc[field.key] = Array.from(options[field.key]).sort((a, b) =>
          a.localeCompare(b)
        );
        return acc;
      },
      {} as Record<QualitativeField, string[]>
    );
  }, [selectedManualDetails]);

  const getManualGroupMatches = useCallback(
    (group: ManualGroup) =>
      selectedManualDetails.filter((detail) =>
        QUALITATIVE_FIELDS.every((field) => {
          const selected = group.filters[field.key];
          if (selected.length === 0) return true;
          return selected.includes(String(detail[field.key] || ''));
        })
      ),
    [selectedManualDetails]
  );

  const manualPreview = useMemo(() => {
    const detailToGroups = new Map<number, string[]>();
    const groupRows = manualGroups.map((group) => {
      const matches = getManualGroupMatches(group);
      matches.forEach((detail) => {
        const current = detailToGroups.get(detail.id) || [];
        current.push(group.name || group.id);
        detailToGroups.set(detail.id, current);
      });

      const totalKilos = matches.reduce(
        (sum, detail) => sum + (Number(detail.kilos) || 0),
        0
      );
      const totalSales = matches.reduce(
        (sum, detail) => sum + (Number(detail.local_total_price) || 0),
        0
      );

      return {
        group,
        matches,
        totalKilos,
        totalSales,
        amountUsd: Number(group.amountUsd) || 0,
      };
    });

    const overlaps = Array.from(detailToGroups.entries())
      .filter(([, groups]) => groups.length > 1)
      .map(([detailId, groups]) => ({ detailId, groups }));

    return { groupRows, overlaps };
  }, [getManualGroupMatches, manualGroups]);

  const resetManualForm = () => {
    setSelectedManualSettlementIds(new Set());
    setManualSettlementSearch('');
    setManualReceiverFilter(new Set());
    setManualGroups([
      {
        id: crypto.randomUUID(),
        name: 'Grupo 1',
        amountUsd: '',
        distribution: 'byKg',
        filters: emptyManualFilters(),
      },
    ]);
    setManualDescription('');
    setManualNotes('');
    setManualExchangeRateMode('per_settlement');
    setManualFixedExchangeRate('');
  };

  const updateSettlementOtherRevenueTotals = async (
    settlementIds: number[]
  ) => {
    const uniqueSettlementIds = [...new Set(settlementIds)];

    for (const settlementId of uniqueSettlementIds) {
      const { data: details } = await supabase
        .from('settlements_details')
        .select('id')
        .eq('settlement_id', settlementId);

      if (details && details.length > 0) {
        const detailIds = details.map((d) => d.id);
        const { data: revenues } = await supabase
          .from('settlement_detail_revenues')
          .select('amount_local')
          .in('settlement_detail_id', detailIds);

        const total = (revenues || []).reduce(
          (sum: number, r: { amount_local?: number | null }) =>
            sum + (r.amount_local || 0),
          0
        );
        await supabase
          .from('settlements')
          .update({ total_other_revenue_local: total })
          .eq('id', settlementId);
      }
    }
  };

  const allocateManualAmounts = (
    amountUsd: number,
    weights: number[]
  ): number[] => {
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    let allocated = 0;

    return weights.map((weight, index) => {
      if (index === weights.length - 1)
        return roundMoney(amountUsd - allocated);
      const amount = roundMoney((amountUsd * weight) / totalWeight);
      allocated += amount;
      return amount;
    });
  };

  const handleSaveManualInjection = async () => {
    if (!userProfile?.exporter_id || !selectedSeason) return;

    if (selectedManualSettlements.length === 0) {
      toast.error(t('Selecciona al menos una liquidación'));
      return;
    }

    if (selectedManualSettlements.some((settlement) => settlement.is_blocked)) {
      toast.error(t('No se puede inyectar en liquidaciones bloqueadas'));
      return;
    }

    if (manualExchangeRateMode === 'fixed') {
      const fixedRate = Number(manualFixedExchangeRate) || 0;
      if (fixedRate <= 0) {
        toast.error(t('Ingresa un tipo de cambio fijo mayor a cero'));
        return;
      }
    }

    if (
      manualExchangeRateMode === 'per_settlement' &&
      selectedManualSettlements.some(
        (settlement) =>
          !settlement.exchange_rate || settlement.exchange_rate <= 0
      )
    ) {
      toast.error(
        t('Todas las liquidaciones deben tener tipo de cambio mayor a cero')
      );
      return;
    }

    if (manualPreview.overlaps.length > 0) {
      toast.error(
        t('Hay líneas que calzan en más de un grupo. Ajusta los filtros.')
      );
      return;
    }

    const validGroups = manualPreview.groupRows.filter(
      ({ amountUsd }) => amountUsd > 0
    );

    if (validGroups.length === 0) {
      toast.error(t('Ingresa al menos un grupo con monto mayor a cero'));
      return;
    }

    for (const row of validGroups) {
      if (row.matches.length === 0) {
        toast.error(`${row.group.name}: ${t('no tiene líneas asociadas')}`);
        return;
      }
      if (row.group.distribution === 'byKg' && row.totalKilos <= 0) {
        toast.error(
          `${row.group.name}: ${t('no tiene kilos para distribuir')}`
        );
        return;
      }
      if (row.group.distribution === 'bySales' && row.totalSales <= 0) {
        toast.error(
          `${row.group.name}: ${t('no tiene venta para distribuir')}`
        );
        return;
      }
    }

    setIsSavingManual(true);
    try {
      const totalUsd = roundMoney(
        validGroups.reduce((sum, row) => sum + row.amountUsd, 0)
      );
      const receiverLabel =
        selectedManualSettlements.length === 1
          ? selectedManualSettlements[0].receiver || 'Manual'
          : 'Múltiples clientes';

      const { data: claimRow, error: claimError } = await supabase
        .from('claims')
        .insert({
          exporter_id: +userProfile.exporter_id,
          receiver: receiverLabel,
          season: selectedSeason,
          status: 'distributed',
          currency: 'USD',
          comparison_type: 'manual_injection',
          description: manualDescription || 'Inyección manual',
          notes: manualNotes || null,
          total_claim_usd: totalUsd,
          total_accepted_usd: totalUsd,
          distributed_at: new Date().toISOString(),
        })
        .select('*')
        .single();

      if (claimError) throw claimError;

      const revenueRows = validGroups.flatMap((row) => {
        const weights = row.matches.map((detail) =>
          row.group.distribution === 'byKg'
            ? Number(detail.kilos) || 0
            : Number(detail.local_total_price) || 0
        );
        const amounts = allocateManualAmounts(row.amountUsd, weights);
        const groupId = crypto.randomUUID();

        return row.matches
          .map((detail, index) => {
            const amountUsd = amounts[index];
            if (amountUsd === 0) return null;
            const exchangeRate =
              manualExchangeRateMode === 'fixed'
                ? Number(manualFixedExchangeRate) || 0
                : Number(detail.settlement.exchange_rate) || 0;

            return {
              settlement_detail_id: detail.id,
              revenue_type: 'manual_injection',
              description:
                row.group.name ||
                manualDescription ||
                `Inyección manual #${claimRow.id}`,
              source:
                manualExchangeRateMode === 'fixed'
                  ? `manual_injection:${claimRow.id}:fixed_rate:${exchangeRate}`
                  : `manual_injection:${claimRow.id}`,
              claim_id: claimRow.id,
              group_id: groupId,
              distribution: row.group.distribution,
              created_by: userProfile?.id ? Number(userProfile.id) : undefined,
              amount_usd: amountUsd,
              amount_local: roundMoney(amountUsd * exchangeRate),
            };
          })
          .filter(Boolean);
      });

      const { error: revenuesError } = await supabase
        .from('settlement_detail_revenues')
        .insert(revenueRows);

      if (revenuesError) throw revenuesError;

      await updateSettlementOtherRevenueTotals(
        selectedManualSettlements.map((settlement) => settlement.id)
      );

      await fetchClaims();
      resetManualForm();
      onManualClose();
      toast.success(t('Inyección manual creada exitosamente'));
    } catch (error) {
      console.error('Error creating manual injection:', error);
      toast.error(t('Error al crear la inyección manual'));
    } finally {
      setIsSavingManual(false);
    }
  };

  const handleViewDetails = (claimId: number) => {
    setLocation(`/settlements/claims/${claimId}`);
  };

  const handleApproveClaim = async () => {
    if (!selectedClaim) return;

    setIsApproving(true);
    try {
      const amountValue = parseFloat(acceptedAmount);
      const updatedClaim = await updateClaimStatusAndAmount(
        selectedClaim.id,
        'accepted',
        amountValue
      );

      // Update the claim in the local state
      setClaims((prevClaims) =>
        prevClaims.map((claim) =>
          claim.id === updatedClaim.id ? updatedClaim : claim
        )
      );

      onClose();
    } catch (error) {
      console.error('Error approving claim:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleStatusChange = async (claimId: number, newStatus: string) => {
    setIsStatusLoading(claimId);
    try {
      // If changing to "accepted" status and claim has total_claim_usd, open modal instead
      const claim = claims.find((c) => c.id === claimId);
      if (
        newStatus === 'accepted' &&
        claim?.total_claim_usd &&
        claim.status !== 'accepted'
      ) {
        setSelectedClaim(claim);
        setAcceptedAmount(claim.total_claim_usd.toString());
        onOpen();
        setIsStatusLoading(null);
        return;
      }

      // For non-accepted status, update with total_accepted_usd = 0
      let updatedClaim;
      if (newStatus !== 'accepted') {
        // Update both status and reset accepted amount to 0
        updatedClaim = await updateClaimStatusAndAmount(claimId, newStatus, 0);
      } else {
        // Just update the status if it's already accepted (shouldn't reach here normally)
        updatedClaim = await updateClaimStatus(claimId, newStatus);
      }

      // Update local state
      setClaims((prevClaims) =>
        prevClaims.map((claim) =>
          claim.id === updatedClaim.id ? updatedClaim : claim
        )
      );
    } catch (error) {
      console.error('Error updating claim status:', error);
    } finally {
      setIsStatusLoading(null);
    }
  };

  const handleDistributeClick = async (claimId: number) => {
    setIsDistributeLoading(claimId);
    try {
      const fullClaim = await getClaimById(claimId);
      setDistributeClaim(fullClaim);
      setExchangeRateMode('per_settlement');
      setFixedExchangeRate('');
      onDistributeOpen();
    } catch (error) {
      console.error('Error fetching claim for distribution:', error);
      toast.error(t('Error al cargar la compensación'));
    } finally {
      setIsDistributeLoading(null);
    }
  };

  const isDistClaimUSD =
    !distributeClaim?.currency || distributeClaim?.currency === 'USD';

  const distributionPreview = useMemo(() => {
    if (!distributeClaim?.claims_details || !distributeClaim.total_accepted_usd)
      return null;

    const totalAccepted = distributeClaim.total_accepted_usd;

    const rows = distributeClaim.claims_details.map((detail) => {
      const ownPerKg =
        detail.own_value_per_kg ??
        (detail.settlements_details?.kilos &&
        detail.settlements_details.usd_total_fob
          ? detail.settlements_details.usd_total_fob /
            detail.settlements_details.kilos
          : null);
      const kilos = detail.settlements_details?.kilos || 0;
      const difference =
        ownPerKg != null
          ? (ownPerKg - detail.comparable_fob_per_kg) * kilos
          : null;
      return { detail, difference };
    });

    const negativeRows = rows.filter(
      (r) => r.difference != null && r.difference < 0
    );
    const totalNegative = negativeRows.reduce(
      (sum, r) => sum + Math.abs(r.difference!),
      0
    );

    if (totalNegative === 0) return null;

    const ratio = totalAccepted / totalNegative;

    const distributions = negativeRows.map(({ detail, difference }) => {
      const portion = Math.abs(difference!) * ratio;
      const rate =
        exchangeRateMode === 'fixed'
          ? parseFloat(fixedExchangeRate) || 0
          : detail.settlements_details?.settlements?.exchange_rate || 0;

      const amountUsd = isDistClaimUSD
        ? portion
        : rate > 0
          ? portion / rate
          : 0;
      const amountLocal = isDistClaimUSD ? portion * rate : portion;

      return {
        detail,
        difference: difference!,
        portion,
        amountUsd,
        amountLocal,
        rate,
      };
    });

    return {
      totalAccepted,
      negativeCount: negativeRows.length,
      totalNegative,
      ratio,
      distributions,
    };
  }, [distributeClaim, exchangeRateMode, fixedExchangeRate, isDistClaimUSD]);

  const fmtDist = (value: number) => {
    if (distributeClaim?.currency && distributeClaim.currency !== 'USD') {
      const formatted = value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${formatted} ${distributeClaim.currency}`;
    }
    return formatMoneyUSD(value);
  };

  const handleDistribute = async () => {
    if (!distributeClaim || !distributionPreview) return;

    setIsDistributing(true);
    try {
      const groupId = crypto.randomUUID();

      const sdrRows = distributionPreview.distributions.map((d) => ({
        settlement_detail_id: d.detail.settlement_detail_id,
        revenue_type: 'claim_compensation',
        description: `Compensación #${distributeClaim.id}`,
        claim_id: distributeClaim.id,
        group_id: groupId,
        created_by: userProfile?.id ? Number(userProfile.id) : undefined,
        amount_usd: d.amountUsd,
        amount_local: d.amountLocal,
      }));

      const { error: insertError } = await supabase
        .from('settlement_detail_revenues')
        .insert(sdrRows);
      if (insertError) throw insertError;

      const uniqueSettlementIds = [
        ...new Set(
          distributionPreview.distributions
            .map((d) => d.detail.settlements_details?.settlements?.id)
            .filter((id): id is number => id != null)
        ),
      ];

      await updateSettlementOtherRevenueTotals(uniqueSettlementIds);

      await distributeClaimStatus(distributeClaim.id);

      // Update local state
      setClaims((prevClaims) =>
        prevClaims.map((c) =>
          c.id === distributeClaim.id ? { ...c, status: 'distributed' } : c
        )
      );

      onDistributeClose();
      setDistributeClaim(null);
      toast.success(t('Distribución completada exitosamente'));
    } catch (error) {
      console.error('Error distributing claim:', error);
      toast.error(t('Error al distribuir la compensación'));
    } finally {
      setIsDistributing(false);
    }
  };

  const handleUndistribute = async (claimId: number) => {
    const confirmed = await confirm({
      title: t('¿Borrar la distribución?'),
      message: t(
        'Se eliminarán los ingresos distribuidos en las liquidaciones asociadas.'
      ),
      confirmLabel: t('Borrar'),
      isDestructive: true,
    });
    if (!confirmed) return;

    setIsUndistributeLoading(claimId);
    try {
      // 1. Find settlement_detail_revenues linked to this claim
      const { data: revenues, error: fetchError } = await supabase
        .from('settlement_detail_revenues')
        .select('id, settlement_detail_id')
        .eq('claim_id', claimId);
      if (fetchError) throw fetchError;

      // 2. Get unique settlement IDs before deleting revenues
      const detailIds = [
        ...new Set((revenues || []).map((r) => r.settlement_detail_id)),
      ];
      let uniqueSettlementIds: number[] = [];

      if (detailIds.length > 0) {
        const { data: details } = await supabase
          .from('settlements_details')
          .select('settlement_id')
          .in('id', detailIds);

        uniqueSettlementIds = [
          ...new Set((details || []).map((d) => d.settlement_id)),
        ];
      }

      // 3. Delete the revenues
      const { error: deleteError } = await supabase
        .from('settlement_detail_revenues')
        .delete()
        .eq('claim_id', claimId);
      if (deleteError) throw deleteError;

      // 4. Recalculate total_other_revenue_local for each affected settlement
      await updateSettlementOtherRevenueTotals(uniqueSettlementIds);

      // 5. Update claim status back to accepted
      await undistributeClaimStatus(claimId);

      // 6. Update local state
      setClaims((prevClaims) =>
        prevClaims.map((c) =>
          c.id === claimId
            ? { ...c, status: 'accepted', distributed_at: null }
            : c
        )
      );

      toast.success(t('Distribución eliminada exitosamente'));
    } catch (error) {
      console.error('Error undistributing claim:', error);
      toast.error(t('Error al borrar la distribución'));
    } finally {
      setIsUndistributeLoading(null);
    }
  };

  const handleDeleteClick = (claim: Claim) => {
    setClaimToDelete(claim);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!claimToDelete) return;

    setIsDeleteLoading(claimToDelete.id);
    try {
      await deleteClaim(claimToDelete.id);
      setClaims((prevClaims) =>
        prevClaims.filter((c) => c.id !== claimToDelete.id)
      );
      toast.success(t('Compensación eliminada exitosamente'));
      onDeleteClose();
      setClaimToDelete(null);
    } catch (error) {
      console.error('Error deleting claim:', error);
      toast.error(t('Error al eliminar la compensación'));
    } finally {
      setIsDeleteLoading(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'warning';
      case 'submitted':
        return 'primary';
      case 'accepted':
        return 'success';
      case 'denied':
        return 'danger';
      case 'cancelled':
        return 'default';
      case 'distributed':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'draft':
        return t('Borrador');
      case 'submitted':
        return t('Enviado');
      case 'accepted':
        return t('Aprobado');
      case 'denied':
        return t('Rechazado');
      case 'cancelled':
        return t('Cancelado');
      case 'distributed':
        return t('Distribuido');
      default:
        return t('Desconocido');
    }
  };

  const getComparisonTypeLabel = (type?: string) => {
    switch (type) {
      case 'byWeeks':
        return t('Por Semanas (Simple)');
      case 'byShips':
        return t('Por Naves (Simple)');
      case 'custom':
        return t('Personalizado (Avanzado)');
      case 'industry':
        return t('Industria');
      case 'sinister_market':
        return t('Siniestro · vs Mercado');
      case 'sinister_exporter':
        return t('Siniestro · vs Exportadora');
      case 'sinister_industry':
        return t('Siniestro · vs Industria');
      case 'manual_injection':
        return t('Inyección manual');
      default:
        return t('No especificado');
    }
  };

  // Calculate total stats grouped by currency
  const totalsByCurrency = filteredClaims.reduce<
    Record<string, { claimed: number; accepted: number }>
  >((acc, claim) => {
    const cur = claim.currency || 'USD';
    if (!acc[cur]) acc[cur] = { claimed: 0, accepted: 0 };
    if (claim.status !== 'cancelled')
      acc[cur].claimed += claim.total_claim_usd || 0;
    acc[cur].accepted += claim.total_accepted_usd || 0;
    return acc;
  }, {});

  const fmtByCurrency = (currency: string, value: number) => {
    if (currency !== 'USD') {
      const formatted = value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${formatted} ${currency}`;
    }
    return formatMoneyUSD(value);
  };

  const fmtClaim = (claim: Claim, value: number) => {
    if (claim.currency && claim.currency !== 'USD') {
      const formatted = value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return `${formatted} ${claim.currency}`;
    }
    return formatMoneyUSD(value);
  };

  const statusOptions = [
    { key: 'draft', label: t('Borrador'), color: 'warning' as const },
    { key: 'submitted', label: t('Enviado'), color: 'primary' as const },
    { key: 'accepted', label: t('Aprobado'), color: 'success' as const },
    { key: 'denied', label: t('Rechazado'), color: 'danger' as const },
    { key: 'cancelled', label: t('Cancelado'), color: 'default' as const },
    {
      key: 'distributed',
      label: t('Distribuido'),
      color: 'secondary' as const,
    },
  ];

  return (
    <div className='flex w-full h-screen overflow-x-hidden'>
      <PageLayout>
        <div>
          <Header
            title={t('Compensaciones')}
            description={t(
              'Gestiona los comparativos guardados entre clientes.'
            )}
            actions={
              <div className='flex items-center gap-2'>
                {/* <InformationTooltip>
                  <div className='p-2'>
                    <h3 className='font-semibold mb-2'>{t('Descripción:')}</h3>
                    <p>
                      {t(
                        'Esta pantalla muestra las compensaciones guardadas de tus clientes. Aquí puede visualizar el estado de cada compensación, el cliente al que corresponde, y el monto total reclamado.'
                      )}
                    </p>
                  </div>
                </InformationTooltip> */}
                <Button
                  color='primary'
                  startContent={<Icon icon='solar:add-circle-linear' />}
                  onPress={onManualOpen}
                  isDisabled={!selectedSeason}
                  variant='solid'
                >
                  {t('Nueva inyección manual')}
                </Button>
              </div>
            }
          />
          {/* <div className='text-sm mt-2 bg-primary-50 border border-primary-200 rounded-lg p-3 flex items-center gap-2 shadow-sm'>
            <Icon icon="solar:bell-bold" className="text-primary-500" width={18} />
            <p className='text-primary-700 font-medium'>
              Pronto, podrás distribuir los montos aceptados en tus liquidaciones.
            </p>
          </div> */}
        </div>

        <div className='space-y-6'>
          <Card shadow='sm'>
            <CardBody className='p-4'>
              <div className='flex flex-col md:flex-row gap-4 justify-between items-center'>
                <div className='flex gap-4 w-full md:w-auto'>
                  <SeasonFilter />
                  <StatusFilter
                    options={statusOptions}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    placeholder={t('Filtrar por Estado')}
                  />
                </div>
                <div className='flex gap-4 w-full md:w-auto'>
                  <Input
                    placeholder={t('Buscar por cliente o notas...')}
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    startContent={
                      <Icon
                        icon='solar:magnifer-linear'
                        className='text-default-300'
                      />
                    }
                    className='w-full md:w-72'
                  />
                </div>
              </div>
            </CardBody>
          </Card>

          {!isLoading && filteredClaims.length > 0 && (
            <div
              className={`grid grid-cols-1 ${Object.keys(totalsByCurrency).length > 1 ? 'md:grid-cols-2' : ''} gap-4`}
            >
              {Object.entries(totalsByCurrency).map(([currency, totals]) => (
                <Card key={currency} shadow='sm'>
                  <CardBody className='p-4'>
                    <div className='flex items-center gap-2 mb-3'>
                      <Chip size='sm' variant='flat' color='default'>
                        {currency}
                      </Chip>
                    </div>
                    <div className='grid grid-cols-2 gap-4'>
                      <div className='flex flex-col'>
                        <span className='text-sm text-default-400'>
                          {t('Total Reclamado')}
                        </span>
                        <span
                          className={`text-xl font-semibold ${totals.claimed < 0 ? 'text-danger-500' : 'text-success-500'}`}
                        >
                          {fmtByCurrency(currency, totals.claimed)}
                        </span>
                      </div>
                      <div className='flex flex-col'>
                        <span className='text-sm text-default-400'>
                          {t('Total Aceptado')}
                        </span>
                        <span
                          className={`text-xl font-semibold ${totals.accepted < 0 ? 'text-danger-500' : 'text-success-500'}`}
                        >
                          {fmtByCurrency(currency, totals.accepted)}
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}

          <Card shadow='sm'>
            <CardBody className='p-4'>
              {isLoading ? (
                <LoadingState />
              ) : filteredClaims.length === 0 ? (
                <EmptyState
                  icon='tabler:file-search'
                  title={t('No se encontraron comparativos guardados')}
                />
              ) : (
                <Table aria-label={t('Tabla de Compensaciones')} removeWrapper>
                  <TableHeader>
                    <TableColumn>ID</TableColumn>
                    <TableColumn>{t('ACCIONES')}</TableColumn>
                    <TableColumn>{t('CLIENTE')}</TableColumn>
                    <TableColumn>{t('DESCRIPCIÓN')}</TableColumn>
                    <TableColumn>{t('TIPO')}</TableColumn>
                    <TableColumn className='text-right'>
                      {t('MONTO RECLAMADO')}
                    </TableColumn>
                    <TableColumn className='text-right'>
                      {t('MONTO ACEPTADO')}
                    </TableColumn>
                    <TableColumn>{t('ESTADO')}</TableColumn>
                    <TableColumn>{t('FECHA CREACIÓN')}</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {filteredClaims.map((claim) => (
                      <TableRow key={claim.id}>
                        <TableCell>#{claim.id}</TableCell>
                        <TableCell>
                          <div className='flex items-center gap-0.5'>
                            <Tooltip content={t('Ver Detalle')}>
                              <Button
                                size='sm'
                                variant='light'
                                isIconOnly
                                onClick={() => handleViewDetails(claim.id)}
                              >
                                <Icon icon='tabler:eye' className='text-default-400' />
                              </Button>
                            </Tooltip>
                            {claim.comparison_type !== 'manual_injection' &&
                              claim.status === 'accepted' &&
                              claim.total_accepted_usd &&
                              claim.total_accepted_usd > 0 && (
                                <Tooltip content={t('Distribuir')}>
                                  <Button
                                    size='sm'
                                    variant='light'
                                    isIconOnly
                                    isLoading={isDistributeLoading === claim.id}
                                    onClick={() =>
                                      handleDistributeClick(claim.id)
                                    }
                                  >
                                    <Icon icon='tabler:share' className='text-secondary' />
                                  </Button>
                                </Tooltip>
                              )}
                            {claim.status === 'distributed' && (
                              <Tooltip content={t('Borrar Distribución')}>
                                <Button
                                  size='sm'
                                  variant='light'
                                  isIconOnly
                                  isLoading={isUndistributeLoading === claim.id}
                                  onClick={() => handleUndistribute(claim.id)}
                                >
                                  <Icon icon='tabler:arrow-back-up' className='text-warning' />
                                </Button>
                              </Tooltip>
                            )}
                            {claim.status !== 'distributed' && (
                              <Tooltip content={t('Eliminar')} color='danger'>
                                <Button
                                  size='sm'
                                  variant='light'
                                  color='danger'
                                  isIconOnly
                                  isLoading={isDeleteLoading === claim.id}
                                  onClick={() => handleDeleteClick(claim)}
                                >
                                  <Icon icon='tabler:trash' className='text-danger' />
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{claim.receiver}</TableCell>
                        <TableCell>{claim.description}</TableCell>
                        <TableCell>
                          {getComparisonTypeLabel(claim.comparison_type)}
                        </TableCell>
                        <TableCell
                          className={
                            claim.total_claim_usd && claim.total_claim_usd < 0
                              ? 'text-right text-danger-500'
                              : 'text-right text-success-500'
                          }
                        >
                          {claim.total_claim_usd
                            ? fmtClaim(claim, claim.total_claim_usd)
                            : 'N/A'}
                        </TableCell>
                        <TableCell
                          className={
                            claim.total_accepted_usd &&
                            claim.total_accepted_usd < 0
                              ? 'text-right text-danger-500'
                              : 'text-right text-success-500'
                          }
                        >
                          {claim.total_accepted_usd
                            ? fmtClaim(claim, claim.total_accepted_usd)
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          {claim.status === 'distributed' ? (
                            <Chip
                              color={getStatusColor(claim.status)}
                              variant='flat'
                              size='sm'
                            >
                              {getStatusLabel(claim.status)}
                            </Chip>
                          ) : (
                            <Dropdown>
                              <DropdownTrigger>
                                <div>
                                  {isStatusLoading === claim.id ? (
                                    <Spinner size='sm' />
                                  ) : (
                                    <Chip
                                      color={getStatusColor(claim.status)}
                                      variant='flat'
                                      size='sm'
                                      className='cursor-pointer'
                                    >
                                      <span className='flex items-center'>
                                        {getStatusLabel(claim.status)}
                                        <Icon
                                          icon='mdi:chevron-down'
                                          className='ml-1'
                                          width={14}
                                        />
                                      </span>
                                    </Chip>
                                  )}
                                </div>
                              </DropdownTrigger>
                              <DropdownMenu
                                aria-label='Status options'
                                onAction={(key) =>
                                  handleStatusChange(claim.id, key.toString())
                                }
                              >
                                {statusOptions
                                  .filter((o) => o.key !== 'distributed')
                                  .map((option) => (
                                    <DropdownItem
                                      key={option.key}
                                      description={
                                        option.key === claim.status
                                          ? t('Estado actual')
                                          : ''
                                      }
                                      startContent={
                                        <Chip
                                          color={option.color}
                                          variant='flat'
                                          size='sm'
                                        >
                                          {option.label}
                                        </Chip>
                                      }
                                    >
                                      {t('Cambiar a')} {option.label}
                                    </DropdownItem>
                                  ))}
                              </DropdownMenu>
                            </Dropdown>
                          )}
                        </TableCell>
                        <TableCell className='whitespace-nowrap'>
                          {new Date(claim.created_at).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardBody>
          </Card>
        </div>
      </PageLayout>

      <Modal
        isOpen={isManualOpen}
        onClose={() => {
          if (!isSavingManual) {
            resetManualForm();
            onManualClose();
          }
        }}
        size='5xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          <ModalHeader>{t('Nueva inyección manual')}</ModalHeader>
          <ModalBody>
            <div className='space-y-5'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                <Input
                  label={t('Descripción')}
                  value={manualDescription}
                  onChange={(e) => setManualDescription(e.target.value)}
                  placeholder={t('Ej: Support comercial temporada')}
                />
                <Textarea
                  label={t('Notas')}
                  value={manualNotes}
                  onChange={(e) => setManualNotes(e.target.value)}
                  placeholder={t('Notas internas opcionales')}
                  minRows={1}
                />
              </div>

              <div className='space-y-3 rounded-medium border border-default-200 p-4'>
                <div>
                  <p className='font-semibold'>{t('Tipo de cambio')}</p>
                  <p className='text-sm text-default-400'>
                    {t(
                      'Por defecto se usa el tipo de cambio de cada liquidación. Usa un tipo fijo solo si quieres forzar la conversión para toda la inyección.'
                    )}
                  </p>
                </div>
                <RadioGroup
                  value={manualExchangeRateMode}
                  onValueChange={(value) =>
                    setManualExchangeRateMode(
                      value as 'per_settlement' | 'fixed'
                    )
                  }
                  orientation='horizontal'
                >
                  <Radio value='per_settlement'>
                    {t('Usar tipo de cambio de cada liquidación')}
                  </Radio>
                  <Radio value='fixed'>
                    {t('Definir tipo de cambio fijo')}
                  </Radio>
                </RadioGroup>
                {manualExchangeRateMode === 'fixed' && (
                  <Input
                    type='number'
                    label={t('Tipo de cambio fijo')}
                    value={manualFixedExchangeRate}
                    onChange={(e) =>
                      setManualFixedExchangeRate(e.target.value)
                    }
                    placeholder='Ej: 950'
                    className='max-w-xs'
                  />
                )}
              </div>

              <div className='space-y-3 rounded-medium border border-default-200 p-4'>
                <div className='flex flex-col gap-1'>
                  <p className='font-semibold'>
                    {t('Liquidaciones afectadas')}
                  </p>
                  <p className='text-sm text-default-400'>
                    {t(
                      'Busca por referencia, contenedor o cliente. También puedes pegar una lista de contenedores o referencias separadas por coma o salto de línea.'
                    )}
                  </p>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                  <Input
                    label={t('Buscar liquidaciones')}
                    value={manualSettlementSearch}
                    onChange={(e) =>
                      setManualSettlementSearch(e.target.value)
                    }
                    placeholder='TTNU000000-0, SEGU111111-1'
                    startContent={
                      <Icon
                        icon='solar:magnifer-linear'
                        className='text-default-300'
                      />
                    }
                    className='md:col-span-2'
                  />
                  <Select
                    label={t('Cliente')}
                    selectionMode='multiple'
                    selectedKeys={manualReceiverFilter}
                    onSelectionChange={(keys) => {
                      if (keys === 'all') {
                        setManualReceiverFilter(
                          new Set(manualReceiverOptions)
                        );
                        return;
                      }
                      setManualReceiverFilter(
                        new Set(
                          Array.from(keys as Set<React.Key>).map(String)
                        )
                      );
                    }}
                    placeholder={t('Todos los clientes')}
                  >
                    {manualReceiverOptions.map((receiver) => (
                      <SelectItem
                        key={receiver}
                        value={receiver}
                        textValue={receiver}
                      >
                        {receiver}
                      </SelectItem>
                    ))}
                  </Select>
                </div>

                <div className='flex flex-col md:flex-row gap-2 md:items-center md:justify-between'>
                  <div className='flex flex-wrap gap-2'>
                    <Chip size='sm' variant='flat'>
                      {filteredManualSettlements.length}{' '}
                      {t('resultados filtrados')}
                    </Chip>
                    <Chip size='sm' variant='flat' color='secondary'>
                      {selectedManualSettlementIds.size} {t('seleccionadas')}
                    </Chip>
                  </div>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      size='sm'
                      variant='flat'
                      onPress={() =>
                        setSelectedManualSettlementIds((prev) => {
                          const next = new Set(prev);
                          filteredManualSettlements.forEach((settlement) => {
                            if (!settlement.is_blocked) {
                              next.add(String(settlement.id));
                            }
                          });
                          return next;
                        })
                      }
                    >
                      {t('Seleccionar filtradas')}
                    </Button>
                    <Button
                      size='sm'
                      variant='flat'
                      onPress={() =>
                        setSelectedManualSettlementIds((prev) => {
                          const next = new Set(prev);
                          filteredManualSettlements.forEach((settlement) =>
                            next.delete(String(settlement.id))
                          );
                          return next;
                        })
                      }
                    >
                      {t('Limpiar filtradas')}
                    </Button>
                    <Button
                      size='sm'
                      variant='light'
                      color='danger'
                      onPress={() =>
                        setSelectedManualSettlementIds(new Set())
                      }
                    >
                      {t('Limpiar todo')}
                    </Button>
                  </div>
                </div>

                {selectedManualSettlements.length > 0 && (
                  <div className='flex flex-wrap gap-2'>
                    {selectedManualSettlements
                      .slice(0, 12)
                      .map((settlement) => (
                        <Chip
                          key={settlement.id}
                          size='sm'
                          variant='flat'
                          color='secondary'
                          onClose={() =>
                            setSelectedManualSettlementIds((prev) => {
                              const next = new Set(prev);
                              next.delete(String(settlement.id));
                              return next;
                            })
                          }
                        >
                          {settlement.reference_number || `#${settlement.id}`}
                          {settlement.container_number
                            ? ` · ${settlement.container_number}`
                            : ''}
                        </Chip>
                      ))}
                    {selectedManualSettlements.length > 12 && (
                      <Chip size='sm' variant='flat'>
                        +{selectedManualSettlements.length - 12}
                      </Chip>
                    )}
                  </div>
                )}

                <div className='max-h-72 overflow-y-auto rounded-lg border border-default-200 divide-y divide-default-100'>
                  {filteredManualSettlements.length === 0 ? (
                    <div className='p-4 text-sm text-default-400'>
                      {t(
                        'No hay liquidaciones que coincidan con los filtros.'
                      )}
                    </div>
                  ) : (
                    filteredManualSettlements
                      .slice(0, 120)
                      .map((settlement) => {
                        const selected = selectedManualSettlementIds.has(
                          String(settlement.id)
                        );

                        return (
                          <button
                            key={settlement.id}
                            type='button'
                            disabled={!!settlement.is_blocked}
                            className={`w-full text-left p-3 transition-colors ${
                              selected
                                ? 'bg-secondary-50 dark:bg-secondary-950'
                                : 'bg-background hover:bg-default-50'
                            } ${settlement.is_blocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={() =>
                              setSelectedManualSettlementIds((prev) => {
                                const next = new Set(prev);
                                const id = String(settlement.id);
                                if (next.has(id)) next.delete(id);
                                else next.add(id);
                                return next;
                              })
                            }
                          >
                            <div className='flex items-start justify-between gap-3'>
                              <div className='min-w-0'>
                                <div className='font-medium truncate'>
                                  {settlement.reference_number ||
                                    `#${settlement.id}`}
                                  {settlement.container_number
                                    ? ` · ${settlement.container_number}`
                                    : ''}
                                </div>
                                <div className='text-xs text-default-500 truncate'>
                                  {settlement.receiver || '-'} ·{' '}
                                  {settlement.date_sold
                                    ? new Date(
                                        settlement.date_sold
                                      ).toLocaleDateString()
                                    : '-'}{' '}
                                  · TC {settlement.exchange_rate || '-'} ·{' '}
                                  {settlement.settlement_details?.length || 0}{' '}
                                  {t('líneas')}
                                </div>
                              </div>
                              <div className='flex items-center gap-2 flex-shrink-0'>
                                {settlement.is_blocked && (
                                  <Chip
                                    size='sm'
                                    color='danger'
                                    variant='flat'
                                  >
                                    {t('Bloqueada')}
                                  </Chip>
                                )}
                                <Chip
                                  size='sm'
                                  color={selected ? 'secondary' : 'default'}
                                  variant='flat'
                                >
                                  {selected
                                    ? t('Seleccionada')
                                    : t('Seleccionar')}
                                </Chip>
                              </div>
                            </div>
                          </button>
                        );
                      })
                  )}
                </div>

                {filteredManualSettlements.length > 120 && (
                  <p className='text-xs text-default-400'>
                    {t(
                      'Mostrando las primeras 120 liquidaciones filtradas. Refina la búsqueda para ver menos resultados o usa “Seleccionar filtradas” para seleccionar todo el subconjunto.'
                    )}
                  </p>
                )}
              </div>

              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='font-semibold'>{t('Grupos de inyección')}</p>
                    <p className='text-sm text-default-400'>
                      {t(
                        'Cada grupo selecciona líneas por atributos cualitativos y recibe un monto USD.'
                      )}
                    </p>
                  </div>
                  <Button
                    size='sm'
                    variant='flat'
                    startContent={<Icon icon='solar:add-circle-linear' />}
                    onPress={() =>
                      setManualGroups((prev) => [
                        ...prev,
                        {
                          id: crypto.randomUUID(),
                          name: `Grupo ${prev.length + 1}`,
                          amountUsd: '',
                          distribution: 'byKg',
                          filters: emptyManualFilters(),
                        },
                      ])
                    }
                  >
                    {t('Agregar grupo')}
                  </Button>
                </div>

                {manualPreview.overlaps.length > 0 && (
                  <Alert variant='danger' showIcon>
                    {t(
                      'Hay líneas que calzan en más de un grupo. Los grupos deben ser excluyentes antes de guardar.'
                    )}
                  </Alert>
                )}

                {manualGroups.map((group) => {
                  const preview = manualPreview.groupRows.find(
                    (row) => row.group.id === group.id
                  );
                  const matches = preview?.matches || [];
                  const distributionBase =
                    group.distribution === 'byKg'
                      ? preview?.totalKilos || 0
                      : preview?.totalSales || 0;

                  return (
                    <div key={group.id} className='space-y-3 rounded-medium border border-default-200 p-4'>
                      <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>
                        <Input
                          label={t('Nombre del grupo')}
                          value={group.name}
                          onChange={(e) =>
                            setManualGroups((prev) =>
                              prev.map((item) =>
                                item.id === group.id
                                  ? { ...item, name: e.target.value }
                                  : item
                              )
                            )
                          }
                        />
                        <Input
                          label={t('Monto USD')}
                          type='number'
                          value={group.amountUsd}
                          onChange={(e) =>
                            setManualGroups((prev) =>
                              prev.map((item) =>
                                item.id === group.id
                                  ? { ...item, amountUsd: e.target.value }
                                  : item
                              )
                            )
                          }
                          startContent={
                            <span className='text-default-400'>USD</span>
                          }
                        />
                        <Select
                          label={t('Distribución')}
                          selectedKeys={[group.distribution]}
                          onSelectionChange={(keys) => {
                            const [value] = Array.from(
                              keys as Set<React.Key>
                            );
                            if (!value) return;
                            setManualGroups((prev) =>
                              prev.map((item) =>
                                item.id === group.id
                                  ? {
                                      ...item,
                                      distribution: String(
                                        value
                                      ) as ManualDistribution,
                                    }
                                  : item
                              )
                            );
                          }}
                        >
                          <SelectItem key='byKg' value='byKg'>
                            {t('Por Kg')}
                          </SelectItem>
                          <SelectItem key='bySales' value='bySales'>
                            {t('Por Venta')}
                          </SelectItem>
                        </Select>
                        <div className='flex items-end'>
                          <Button
                            color='danger'
                            variant='light'
                            isDisabled={manualGroups.length === 1}
                            onPress={() =>
                              setManualGroups((prev) =>
                                prev.filter((item) => item.id !== group.id)
                              )
                            }
                          >
                            {t('Eliminar')}
                          </Button>
                        </div>
                      </div>

                      <div className='grid grid-cols-1 md:grid-cols-4 gap-3'>
                        {QUALITATIVE_FIELDS.map((field) => (
                          <Select
                            key={`${group.id}-${field.key}`}
                            label={field.label}
                            selectionMode='multiple'
                            selectedKeys={new Set(group.filters[field.key])}
                            onSelectionChange={(keys) => {
                              const values =
                                keys === 'all'
                                  ? manualFieldOptions[field.key]
                                  : Array.from(keys as Set<React.Key>).map(
                                      String
                                    );
                              setManualGroups((prev) =>
                                prev.map((item) =>
                                  item.id === group.id
                                    ? {
                                        ...item,
                                        filters: {
                                          ...item.filters,
                                          [field.key]: values,
                                        },
                                      }
                                    : item
                                )
                              );
                            }}
                            placeholder={t('Todos')}
                          >
                            {manualFieldOptions[field.key].map((value) => (
                              <SelectItem
                                key={value}
                                value={value}
                                textValue={value}
                              >
                                {value}
                              </SelectItem>
                            ))}
                          </Select>
                        ))}
                      </div>

                      <div className='flex flex-wrap gap-2 text-sm text-default-500'>
                        <Chip size='sm' variant='flat'>
                          {matches.length} {t('líneas')}
                        </Chip>
                        <Chip size='sm' variant='flat'>
                          {Number(preview?.totalKilos || 0).toLocaleString()}{' '}
                          kg
                        </Chip>
                        <Chip
                          size='sm'
                          variant='flat'
                          color={distributionBase > 0 ? 'default' : 'warning'}
                        >
                          {group.distribution === 'byKg'
                            ? t('Base Kg')
                            : t('Base Venta')}
                          : {distributionBase.toLocaleString()}
                        </Chip>
                      </div>

                      {matches.length > 0 && (
                        <Table
                          aria-label={`Preview ${group.name}`}
                          isCompact
                          removeWrapper
                        >
                          <TableHeader>
                            <TableColumn>{t('LIQUIDACIÓN')}</TableColumn>
                            <TableColumn>{t('VARIEDAD')}</TableColumn>
                            <TableColumn>{t('CALIBRE')}</TableColumn>
                            <TableColumn>{t('COLOR')}</TableColumn>
                            <TableColumn>KILOS</TableColumn>
                            <TableColumn>{t('VENTA')}</TableColumn>
                          </TableHeader>
                          <TableBody>
                            {matches.slice(0, 8).map((detail) => (
                              <TableRow key={`${group.id}-${detail.id}`}>
                                <TableCell>
                                  {detail.settlement.reference_number ||
                                    `#${detail.settlement.id}`}
                                </TableCell>
                                <TableCell>{detail.variety || '-'}</TableCell>
                                <TableCell>{detail.caliber || '-'}</TableCell>
                                <TableCell>{detail.color || '-'}</TableCell>
                                <TableCell>
                                  {(detail.kilos || 0).toLocaleString()}
                                </TableCell>
                                <TableCell>
                                  {(
                                    detail.local_total_price || 0
                                  ).toLocaleString()}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                      {matches.length > 8 && (
                        <p className='text-xs text-default-400'>
                          {t('Mostrando 8 líneas de')} {matches.length}.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className='rounded-lg bg-default-100 p-3 flex flex-col md:flex-row gap-2 md:items-center md:justify-between'>
                <span className='text-sm text-default-500'>
                  {t('Total a inyectar')}
                </span>
                <span className='text-xl font-bold text-success-600'>
                  {formatMoneyUSD(
                    manualPreview.groupRows.reduce(
                      (sum, row) => sum + (row.amountUsd || 0),
                      0
                    )
                  )}
                </span>
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant='light'
              onPress={() => {
                resetManualForm();
                onManualClose();
              }}
              isDisabled={isSavingManual}
            >
              {t('Cancelar')}
            </Button>
            <Button
              color='primary'
              onPress={handleSaveManualInjection}
              isLoading={isSavingManual}
            >
              {t('Guardar inyección')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          {selectedClaim && (
            <>
              <ModalHeader>
                {t('Aprobar Compensación')} #{selectedClaim.id}
              </ModalHeader>
              <ModalBody>
                <div className='space-y-4'>
                  <div>
                    <p className='text-sm text-default-400'>{t('Cliente')}</p>
                    <p className='font-medium'>{selectedClaim.receiver}</p>
                  </div>
                  <div>
                    <p className='text-sm text-default-400'>
                      {t('Monto Reclamado')}
                    </p>
                    <p
                      className={`font-medium ${selectedClaim.total_claim_usd && selectedClaim.total_claim_usd < 0 ? 'text-danger-500' : 'text-success-500'}`}
                    >
                      {selectedClaim.total_claim_usd
                        ? fmtClaim(selectedClaim, selectedClaim.total_claim_usd)
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className='text-sm text-default-400'>
                      {t('Monto a Aprobar')}
                    </p>
                    <Input
                      type='number'
                      value={acceptedAmount}
                      onChange={(e) => setAcceptedAmount(e.target.value)}
                      startContent={
                        <span className='text-default-400'>
                          {selectedClaim.currency || 'USD'}
                        </span>
                      }
                      placeholder={t('Ingrese el monto aprobado')}
                    />
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant='light' onClick={onClose}>
                  {t('Cancelar')}
                </Button>
                <Button
                  color='primary'
                  onClick={handleApproveClaim}
                  isLoading={isApproving}
                >
                  {t('Aprobar')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Distribute Modal */}
      <Modal
        isOpen={isDistributeOpen}
        onClose={() => {
          onDistributeClose();
          setDistributeClaim(null);
        }}
        size='4xl'
        scrollBehavior='inside'
      >
        <ModalContent>
          {distributeClaim && (
            <>
              <ModalHeader>
                {t('Distribuir Compensación')} #{distributeClaim.id}
              </ModalHeader>
              <ModalBody>
                {distributionPreview ? (
                  <div className='space-y-4'>
                    <div className='bg-default-100 rounded-lg p-4'>
                      <p className='text-sm text-default-400'>
                        {t('Monto total aceptado')}
                      </p>
                      <p className='text-xl font-bold'>
                        {fmtDist(distributionPreview.totalAccepted)}
                      </p>
                    </div>

                    <div>
                      <p className='font-semibold mb-2'>
                        {isDistClaimUSD
                          ? t('Conversión a moneda local')
                          : t('Conversión a USD')}
                      </p>
                      <RadioGroup
                        value={exchangeRateMode}
                        onValueChange={(val) =>
                          setExchangeRateMode(val as 'fixed' | 'per_settlement')
                        }
                      >
                        <Radio value='per_settlement'>
                          {t('Usar tipo de cambio de cada liquidación')}
                        </Radio>
                        <Radio value='fixed'>
                          {t('Definir tipo de cambio fijo')}
                        </Radio>
                      </RadioGroup>
                      {exchangeRateMode === 'fixed' && (
                        <Input
                          type='number'
                          label={t('Tipo de cambio')}
                          placeholder='Ej: 950'
                          value={fixedExchangeRate}
                          onChange={(e) => setFixedExchangeRate(e.target.value)}
                          className='mt-2 max-w-xs'
                        />
                      )}
                    </div>

                    <div className='bg-default-50 border border-default-200 rounded-lg p-3'>
                      <p className='text-sm'>
                        <strong>{distributionPreview.negativeCount}</strong>{' '}
                        {t('filas negativas con un total de')}{' '}
                        <strong>
                          {fmtDist(distributionPreview.totalNegative)}
                        </strong>
                        . {t('Ratio de distribución')}:{' '}
                        <strong>
                          {(distributionPreview.ratio * 100).toFixed(2)}%
                        </strong>
                      </p>
                    </div>

                    <Table aria-label='Distribution preview' isCompact>
                      <TableHeader>
                        <TableColumn>{t('VARIEDAD')}</TableColumn>
                        <TableColumn>{t('CALIBRE')}</TableColumn>
                        <TableColumn>KILOS</TableColumn>
                        <TableColumn>
                          {t('DIFERENCIA')} ({distributeClaim.currency || 'USD'}
                          )
                        </TableColumn>
                        <TableColumn>
                          {t('MONTO ASIGNADO')} (
                          {distributeClaim.currency || 'USD'})
                        </TableColumn>
                        <TableColumn>
                          {isDistClaimUSD ? t('MONTO LOCAL') : t('MONTO USD')}
                        </TableColumn>
                        <TableColumn>T/C</TableColumn>
                      </TableHeader>
                      <TableBody>
                        {[
                          ...distributionPreview.distributions.map((d, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                {d.detail.settlements_details?.variety || '-'}
                              </TableCell>
                              <TableCell>
                                {d.detail.settlements_details?.caliber || '-'}
                              </TableCell>
                              <TableCell>
                                {d.detail.settlements_details?.kilos?.toLocaleString() ||
                                  '-'}
                              </TableCell>
                              <TableCell className='text-danger-500'>
                                {fmtDist(d.difference)}
                              </TableCell>
                              <TableCell className='text-success-500'>
                                {fmtDist(d.portion)}
                              </TableCell>
                              <TableCell>
                                {isDistClaimUSD
                                  ? d.amountLocal.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                  : formatMoneyUSD(d.amountUsd)}
                              </TableCell>
                              <TableCell>
                                {d.rate
                                  ? d.rate.toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          )),
                          <TableRow
                            key='totals'
                            className='font-bold bg-default-100'
                          >
                            <TableCell>TOTAL</TableCell>
                            <TableCell>{''}</TableCell>
                            <TableCell>
                              {distributionPreview.distributions
                                .reduce(
                                  (s, d) =>
                                    s +
                                    (d.detail.settlements_details?.kilos || 0),
                                  0
                                )
                                .toLocaleString()}
                            </TableCell>
                            <TableCell className='text-danger-500'>
                              {fmtDist(
                                distributionPreview.distributions.reduce(
                                  (s, d) => s + d.difference,
                                  0
                                )
                              )}
                            </TableCell>
                            <TableCell className='text-success-500'>
                              {fmtDist(
                                distributionPreview.distributions.reduce(
                                  (s, d) => s + d.portion,
                                  0
                                )
                              )}
                            </TableCell>
                            <TableCell>
                              {isDistClaimUSD
                                ? distributionPreview.distributions
                                    .reduce((s, d) => s + d.amountLocal, 0)
                                    .toLocaleString(undefined, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })
                                : formatMoneyUSD(
                                    distributionPreview.distributions.reduce(
                                      (s, d) => s + d.amountUsd,
                                      0
                                    )
                                  )}
                            </TableCell>
                            <TableCell>{''}</TableCell>
                          </TableRow>,
                        ]}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <EmptyState
                    title={t('No hay filas negativas para distribuir.')}
                  />
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='light'
                  onClick={() => {
                    onDistributeClose();
                    setDistributeClaim(null);
                  }}
                >
                  {t('Cancelar')}
                </Button>
                <Button
                  color='primary'
                  onClick={handleDistribute}
                  isLoading={isDistributing}
                  isDisabled={
                    !distributionPreview ||
                    (exchangeRateMode === 'fixed' &&
                      (!fixedExchangeRate ||
                        parseFloat(fixedExchangeRate) <= 0))
                  }
                >
                  {t('Confirmar Distribución')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => {
          onDeleteClose();
          setClaimToDelete(null);
        }}
      >
        <ModalContent>
          {claimToDelete && (
            <>
              <ModalHeader>
                {t('Eliminar Compensación')} #{claimToDelete.id}
              </ModalHeader>
              <ModalBody>
                <div className='space-y-3'>
                  <p>
                    {t(
                      '¿Estás seguro de que deseas eliminar esta compensación? Esta acción no se puede deshacer.'
                    )}
                  </p>
                  <div className='bg-default-100 rounded-lg p-3'>
                    <p className='text-sm text-default-400'>{t('Cliente')}</p>
                    <p className='font-medium'>{claimToDelete.receiver}</p>
                  </div>
                  {claimToDelete.total_claim_usd && (
                    <div className='bg-default-100 rounded-lg p-3'>
                      <p className='text-sm text-default-400'>
                        {t('Monto Reclamado')}
                      </p>
                      <p className='font-medium'>
                        {fmtClaim(claimToDelete, claimToDelete.total_claim_usd)}
                      </p>
                    </div>
                  )}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='light'
                  onClick={() => {
                    onDeleteClose();
                    setClaimToDelete(null);
                  }}
                >
                  {t('Cancelar')}
                </Button>
                <Button
                  color='danger'
                  onClick={handleDeleteConfirm}
                  isLoading={isDeleteLoading === claimToDelete.id}
                >
                  {t('Eliminar')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ClaimsPage;
